import {
  GIT_REPO_ATTACHMENT_ID,
  type GitRepoAttachmentConfig,
  type CustomSandboxSize,
  type PluginAttachmentValue,
  type ResourceSize,
} from '@runloop/reflex-contract';
import {
  DEVBOX_CAPABILITIES,
  type DevboxCapabilityId,
  type AgentEnvVar,
  type PromptMode,
} from '@reflex/shared';
import type { AgentType, Attachment } from '@/lib/types';
import type { AgentPersona } from '@reflex/plugin-agent-persona/shared/types';
import { mergePersonaPrompt } from '@reflex/plugin-agent-persona/shared/prompt-merge';

export interface LaunchFormState {
  agentType: AgentType;
  selectedProviderId: string | null;
  model: string;
  modelEndpointId: string | null;
  manualBlueprintName: string | null;
  /**
   * Devbox snapshot (`snp_*`) to launch from instead of a blueprint. The
   * picker keeps this mutually exclusive with `manualBlueprintName` — the
   * server rejects launches that set both.
   */
  snapshotId: string | null;
  /**
   * Devbox size override picked in the Advanced section, or `null` to fall
   * back to the blueprint's configured size at launch time. Part of the
   * sandbox-provider options the launcher sends to the agent runner.
   */
  resourceSize: ResourceSize | null;
  /**
   * Org-defined custom devbox size picked in the Advanced section, mutually
   * exclusive with `resourceSize`. Snapshotted from the org's
   * `customSandboxSizes` at pick time so the launch carries the exact
   * dimensions even if the org's list changes later.
   */
  customSize: CustomSandboxSize | null;
  /**
   * Start a Docker daemon (Docker-in-Docker) in the devbox at launch. Part of
   * the sandbox-provider options the launcher sends to the agent runner.
   */
  dockerd: boolean;
  /**
   * Enable computer use / browser use (a virtual desktop) at launch. Part of
   * the sandbox-provider options; gated on the computer-use plugin being
   * installed for the org.
   */
  computerUse: boolean;
  /**
   * Minutes the devbox may sit idle before auto-suspending. `null` defers to
   * the sandbox provider's default ({@link DEFAULT_SANDBOX_IDLE_TIME_MINUTES}).
   */
  idleTimeMinutes: number | null;
  attachmentValues: Record<string, PluginAttachmentValue>;
  envVars: AgentEnvVar[];
  chatInputKey: number;
  selectedPersonaId: string | null;
  systemPrompt: string | null;
  initialPrompt: string;
  chatAttachments: Attachment[];
  /**
   * `mps_*` id of the model-provider secret the user picked for the launch
   * (apiKey or subscription). The server uses this exact key and derives
   * the legacy auth method from the resolved row's type. `null` until the
   * user picks one — launch is gated on a non-null value.
   */
  selectedProviderSecretId: string | null;
  reasoningEffort: string | null;
  promptMode: PromptMode | null;
  /**
   * Labels (`alb_*`) the launch puts on the run, and the group (`agp_*`) it
   * files the run into. Both are ids from the org's catalogs; the pickers
   * create anything new before it lands here.
   */
  labelIds: string[];
  groupId: string | null;
}

export type LaunchFormAction =
  | {
      type: 'set_agent_type';
      agentType: AgentType;
      defaultProviderId: string | null;
      defaultModel: string;
      defaultEndpointRouteId: string | null;
    }
  | { type: 'set_field'; field: keyof LaunchFormState; value: unknown }
  | { type: 'set_sandbox'; resourceSize?: ResourceSize; capabilityId?: DevboxCapabilityId }
  | {
      type: 'set_model';
      model: string;
      providerId: string;
      defaultEndpointRouteId: string | null;
    }
  | { type: 'set_attachment'; id: string; value: unknown; pluginName: string }
  | { type: 'set_env_vars'; envVars: AgentEnvVar[] }
  | { type: 'apply_persona'; persona: AgentPersona }
  | {
      type: 'clear_persona';
      agentType: AgentType;
      defaultModel: string;
      defaultEndpointRouteId: string | null;
    }
  | {
      type: 'reset';
      agentType: AgentType;
      defaultModel: string;
      defaultEndpointRouteId: string | null;
    }
  | {
      type: 'load_persisted';
      persistedData: {
        agentType: AgentType;
        selectedProviderId: string | null;
        model: string;
        modelEndpointId: string | null;
        manualBlueprintName: string | null;
        snapshotId?: string | null;
        resourceSize?: ResourceSize | null;
        customSize?: CustomSandboxSize | null;
        dockerd?: boolean;
        computerUse?: boolean;
        idleTimeMinutes?: number | null;
        attachmentValues: Record<string, PluginAttachmentValue>;
        envVars: AgentEnvVar[];
        initialPrompt: string;
        selectedPersonaId: string | null;
        systemPrompt: string | null;
        chatAttachments: Attachment[];
        selectedProviderSecretId: string | null;
        reasoningEffort?: string | null;
        promptMode?: PromptMode | null;
        labelIds?: string[];
        groupId?: string | null;
      };
    }
  | { type: 'set_chat_attachments'; attachments: Attachment[] }
  | { type: 'set_prompt'; prompt: string };

/**
 * Seed values for the launch dialog. Used by the "Launch again" action to
 * pre-fill the dialog from an existing agent's configuration and by the
 * guided-demo runtime to seed the inline launch form.
 */
export interface LaunchAgentDialogDefaults {
  agentType?: AgentType;
  selectedProviderId?: string | null;
  selectedProviderSecretId?: string | null;
  model?: string;
  modelEndpointId?: string | null;
  manualBlueprintName?: string | null;
  snapshotId?: string | null;
  resourceSize?: ResourceSize | null;
  customSize?: CustomSandboxSize | null;
  /** Devbox capabilities to switch on at launch (see {@link DEVBOX_CAPABILITIES}). */
  dockerd?: boolean;
  computerUse?: boolean;
  idleTimeMinutes?: number | null;
  attachmentValues?: Record<string, PluginAttachmentValue>;
  envVars?: AgentEnvVar[];
  initialPrompt?: string;
  selectedPersonaId?: string | null;
  systemPrompt?: string | null;
  chatAttachments?: Attachment[];
  reasoningEffort?: string | null;
  promptMode?: PromptMode | null;
  labelIds?: string[];
  groupId?: string | null;
}

/**
 * Every field of the form state must be seedable. A field that exists in
 * {@link LaunchFormState} but not here cannot be carried into a launch, and
 * the failure is silent — "Launch again" quietly starts a run configured
 * differently from the one it copied. `chatInputKey` is the one exemption: it
 * is the composer's remount counter, owned by the reducer.
 *
 * Compile-time only. Adding a state field without a default here resolves to
 * that field's name instead of `never`, and this line stops type-checking.
 */
type AssertNever<T extends never> = T;
export type _AllFormStateFieldsAreSeedable = AssertNever<
  Exclude<keyof LaunchFormState, 'chatInputKey' | keyof LaunchAgentDialogDefaults>
>;

/**
 * Apply one-shot attachment defaults on top of a launch configuration.
 *
 * Route handoffs use this to add the resource the user just acted on without
 * discarding unrelated attachments restored from their saved launch settings.
 * A matching attachment id is intentionally replaced by the handoff value.
 */
export function mergeAttachmentDefaults(
  base: Record<string, PluginAttachmentValue> | undefined,
  defaults: Record<string, PluginAttachmentValue> | undefined,
): Record<string, PluginAttachmentValue> {
  return { ...(base ?? {}), ...(defaults ?? {}) };
}

/**
 * What {@link createInitialState} takes: the seed values, with the two fields
 * a caller must resolve before the form exists made required.
 *
 * Derived from {@link LaunchAgentDialogDefaults} rather than restated so the
 * two lists cannot drift — a seedable field added to one is accepted by the
 * other for free.
 */
export type CreateInitialStateOptions = Omit<LaunchAgentDialogDefaults, 'agentType' | 'model'> & {
  agentType: AgentType;
  model: string;
  /** Remount key for the composer. Reducer-internal; never seeded. */
  chatInputKey?: number;
};

export function createInitialState(opts: CreateInitialStateOptions): LaunchFormState {
  return {
    agentType: opts.agentType,
    selectedProviderId: opts.selectedProviderId ?? null,
    model: opts.model,
    modelEndpointId: opts.modelEndpointId ?? null,
    manualBlueprintName: opts.manualBlueprintName ?? null,
    snapshotId: opts.snapshotId ?? null,
    resourceSize: opts.resourceSize ?? null,
    customSize: opts.customSize ?? null,
    dockerd: opts.dockerd ?? false,
    computerUse: opts.computerUse ?? false,
    idleTimeMinutes: opts.idleTimeMinutes ?? null,
    attachmentValues: opts.attachmentValues ?? {},
    envVars: opts.envVars ?? [],
    chatInputKey: opts.chatInputKey ?? 0,
    selectedPersonaId: opts.selectedPersonaId ?? null,
    systemPrompt: opts.systemPrompt ?? null,
    initialPrompt: opts.initialPrompt ?? '',
    chatAttachments: opts.chatAttachments ?? [],
    selectedProviderSecretId: opts.selectedProviderSecretId ?? null,
    reasoningEffort: opts.reasoningEffort ?? null,
    promptMode: opts.promptMode ?? null,
    labelIds: opts.labelIds ?? [],
    groupId: opts.groupId ?? null,
  };
}

export function launchFormReducer(
  state: LaunchFormState,
  action: LaunchFormAction,
): LaunchFormState {
  switch (action.type) {
    case 'set_agent_type':
      return {
        ...state,
        agentType: action.agentType,
        selectedProviderId: action.defaultProviderId,
        model: action.defaultModel,
        modelEndpointId: action.defaultEndpointRouteId,
        // Reset the picked key when changing agent type — the new type's
        // default model may target a different provider, making the prior
        // selection invalid. The picker re-applies the remembered pick for
        // the new provider on the next render.
        selectedProviderSecretId: null,
        reasoningEffort: null,
        promptMode: null,
      };
    case 'set_field':
      return { ...state, [action.field]: action.value };
    case 'set_sandbox': {
      const next: LaunchFormState = { ...state };
      if (action.resourceSize) {
        next.resourceSize = action.resourceSize;
        // A named size and an org-defined custom size are mutually exclusive —
        // picking one clears the other so the launch carries a single size.
        next.customSize = null;
      }
      if (action.capabilityId) {
        // Resolve the referenced capability to its sandbox flag and enable it;
        // an unknown id is ignored rather than trusted.
        const capability = DEVBOX_CAPABILITIES.find((c) => c.id === action.capabilityId);
        if (capability) next[capability.field] = true;
      }
      return next;
    }
    case 'set_model':
      return {
        ...state,
        selectedProviderId: action.providerId,
        model: action.model,
        modelEndpointId: action.defaultEndpointRouteId,
        selectedProviderSecretId:
          state.selectedProviderId === action.providerId ? state.selectedProviderSecretId : null,
      };
    case 'set_attachment': {
      const next = { ...state.attachmentValues };
      if (action.value === null) {
        delete next[action.id];
      } else {
        next[action.id] = {
          attachmentId: action.id,
          pluginName: action.pluginName,
          config: action.value,
        };
      }
      return { ...state, attachmentValues: next };
    }
    case 'set_env_vars':
      return { ...state, envVars: action.envVars };
    case 'apply_persona': {
      const { persona } = action;
      // Persona attachments already include the `git-repo` attachment when
      // the persona pins a repo — copy them straight into the form state.
      const attachmentValues: Record<string, PluginAttachmentValue> = Object.fromEntries(
        (persona.attachments ?? []).map((pa) => [pa.attachmentId, pa]),
      );
      const envVars: AgentEnvVar[] = (persona.envVars ?? []).map((entry) => ({ ...entry }));
      // Layer the persona's sandboxOptions onto the form so the launcher
      // ships the persona's blueprint + devbox size unchanged when nothing
      // else is touched. The legacy `blueprintName` fallback keeps personas
      // saved before the sandboxOptions column intact.
      const personaSandbox = persona.sandboxOptions ?? null;
      // A persona pins EITHER a snapshot or a blueprint (mutually exclusive,
      // like the picker + the server's both-set 400). A snapshot-pinned persona
      // applies its `snapshotId` and leaves the blueprint cleared; otherwise the
      // blueprint applies (with the legacy `blueprintName` fallback for personas
      // saved before the sandboxOptions column).
      const personaSnapshotId = personaSandbox?.snapshotId ?? null;
      const blueprintName = personaSnapshotId
        ? null
        : (personaSandbox?.blueprintName ?? persona.blueprintName ?? null);
      const resourceSize = personaSandbox?.resourceSize ?? null;
      const customSize = personaSandbox?.customSize ?? null;
      const dockerd = personaSandbox?.dockerd ?? false;
      const computerUse = personaSandbox?.computerUse ?? false;
      const idleTimeMinutes = personaSandbox?.idleTimeMinutes ?? null;
      // Preserve the current prompt text — persona selection should not clear
      // the user's draft. Do not bump chatInputKey so the ChatInput keeps its
      // local state (cursor position, selection, etc.).
      return {
        ...state,
        selectedPersonaId: persona.id,
        // `defaultPrompt` is folded into `systemPrompt` here only; submit and
        // relaunch must not re-merge it.
        systemPrompt:
          mergePersonaPrompt(persona.defaultPrompt, persona.systemPrompt ?? undefined) || null,
        promptMode: persona.promptMode ?? null,
        agentType: persona.agentType,
        selectedProviderId: null,
        model: persona.model ?? state.model,
        manualBlueprintName: blueprintName,
        snapshotId: personaSnapshotId,
        resourceSize,
        customSize,
        dockerd,
        computerUse,
        idleTimeMinutes,
        attachmentValues,
        envVars,
        // Persona-owned like attachments and env vars: applying one replaces
        // what was picked by hand rather than merging into it, so the form
        // shows exactly what the persona will launch.
        labelIds: persona.labelIds ?? [],
        groupId: persona.groupId ?? null,
      };
    }
    case 'clear_persona':
      // Clear persona-owned config but preserve the prompt draft and chat
      // attachments. Do not bump chatInputKey so the ChatInput keeps its state.
      // Reset authMethod since the new agent type may not support claude-max.
      return {
        ...state,
        agentType: action.agentType,
        selectedProviderId: null,
        model: action.defaultModel,
        modelEndpointId: action.defaultEndpointRouteId,
        manualBlueprintName: null,
        snapshotId: null,
        resourceSize: null,
        customSize: null,
        dockerd: false,
        computerUse: false,
        idleTimeMinutes: null,
        attachmentValues: {},
        envVars: [],
        labelIds: [],
        groupId: null,
        selectedPersonaId: null,
        systemPrompt: null,
        selectedProviderSecretId: null,
        reasoningEffort: null,
        promptMode: null,
      };
    case 'reset':
      // Bump chatInputKey so the ChatInput remounts fresh. The dialog passes
      // `preserveOnSubmit`, so the composer keeps its text/attachments after a
      // submit (for easy retry on error); remounting on reset is what actually
      // clears it after a successful launch and on the Clear action.
      return createInitialState({
        agentType: action.agentType,
        model: action.defaultModel,
        modelEndpointId: action.defaultEndpointRouteId,
        chatInputKey: state.chatInputKey + 1,
      });
    case 'load_persisted':
      return {
        ...state,
        ...action.persistedData,
        // Increment chat input key to force re-render with new initial prompt and attachments
        chatInputKey: state.chatInputKey + 1,
      };
    case 'set_chat_attachments':
      return {
        ...state,
        chatAttachments: action.attachments,
      };
    case 'set_prompt':
      // Set the composer text and bump chatInputKey so the ChatInput remounts
      // with the new value — it reads `initialValue` only on mount, so writing
      // `initialPrompt` alone wouldn't show up. Used by the guided tour's
      // `data-tutorial-launch-prompt` signal to pre-fill the task so the user
      // only has to hit Launch.
      return {
        ...state,
        initialPrompt: action.prompt,
        chatInputKey: state.chatInputKey + 1,
      };
  }
}

/**
 * Pull the `git-repo` attachment config out of the current form state so the
 * Launch dialog can still filter blueprints by repo.
 */
export function readGitRepoAttachment(
  attachmentValues: Record<string, PluginAttachmentValue>,
): GitRepoAttachmentConfig | null {
  const entry = attachmentValues[GIT_REPO_ATTACHMENT_ID];
  if (!entry) return null;
  const config = entry.config as GitRepoAttachmentConfig | null;
  if (!config?.repoSlug) return null;
  return config;
}

// Re-export shared helpers from the canonical plugin-api location.
export { hasMeaningfulConfig } from '@reflex/ui/client/agent-config-utils';
